import { useState, useCallback } from 'react';

// Present stacking contract (lowest -> highest).
// `.app-scale` uses transform: scale(...), so in-tree values only compete inside
// the Present/app-scale stacking context. Body-portaled alerts and modals escape it.
// Content-box expand viewers (DraggableResizableViewer) portal to
// `.present-content-root` so they can use PRESENT_QUICK_LAUNCH_BASE_Z_INDEX
// above the header row (z-index 2) instead of being trapped in the stage (1).
//
// Annotation layers (see present.css `.present-annotation-overlay*`):
//   idle base ink -> PRESENT_ANNOTATION_IDLE_Z_INDEX (under widgets + Quick Launch)
//   active base drawing -> PRESENT_ANNOTATION_ACTIVE_Z_INDEX (over widgets, under QL)
//   Quick Launch / agenda floaters own a surface overlay inside the viewer
export const PRESENT_STAGE_Z_INDEX = 1;
export const PRESENT_ANNOTATION_IDLE_Z_INDEX = 950;
export const PRESENT_CHROME_Z_INDEX = 1000;
export const PRESENT_DATE_PICKER_OPEN_Z_INDEX = 1306;
export const PRESENT_ACTIVE_CONTROLS_Z_INDEX = 1250;
export const PRESENT_STYLE_ROW_Z_INDEX = PRESENT_CHROME_Z_INDEX;
export const PRESENT_NO_CONTENT_ALERT_Z_INDEX = 1001;
export const PRESENT_AUTOMATION_BACKDROP_Z_INDEX = 1040;
export const PRESENT_WIDGET_BASE_Z_INDEX = 1050;
export const PRESENT_AUTOMATION_RUNTIME_Z_INDEX = 1051;
export const PRESENT_ANNOTATION_ACTIVE_Z_INDEX = 1090;
export const PRESENT_QUICK_LAUNCH_BASE_Z_INDEX = 1100;
export const PRESENT_FULLSCREEN_WIDGET_BASE_Z_INDEX = 1200;
export const PRESENT_OVERLAY_ALERT_Z_INDEX = 10050;
export const PRESENT_BLOCKING_ALERT_Z_INDEX = 11000;
export const PRESENT_ADVANCE_NOTICE_Z_INDEX = 12050;

export function getWidgetStackZIndex(stack = [], id, baseZ = PRESENT_WIDGET_BASE_Z_INDEX, step = 2) {
  const idx = stack.indexOf(id);
  return baseZ + (idx === -1 ? 0 : idx) * step;
}

export function useWidgetStack(baseZ = PRESENT_WIDGET_BASE_Z_INDEX, step = 2) {
  const [stack, setStack] = useState([]);

  // Add a widget to the top of the stack (call when opening)
  const push = useCallback((id) => {
    setStack(prev => [...prev.filter(w => w !== id), id]);
  }, []);

  // Remove a widget from the stack (call when closing)
  const remove = useCallback((id) => {
    setStack(prev => prev.filter(w => w !== id));
  }, []);

  // Move an already-open widget to the top (call on user interaction)
  const bringToFront = useCallback((id) => {
    setStack(prev => {
      if (prev[prev.length - 1] === id) return prev; // already on top, skip re-render
      return [...prev.filter(w => w !== id), id];
    });
  }, []);

  // Reset the entire stack (call when switching classes)
  const clear = useCallback(() => setStack([]), []);

  // Returns the z-index for the widget's main wrapper.
  // The overlay (drag/resize capture layer) should use getZIndex(id) + 1.
  const getZIndex = useCallback((id) => {
    return getWidgetStackZIndex(stack, id, baseZ, step);
  }, [stack, baseZ, step]);

  return { push, remove, bringToFront, getZIndex, clear };
}
